/**
 * Attachment React Query Hooks
 * 
 * Hooks for fetching and mutating attachments with optimistic updates
 * and proper cache invalidation.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import * as attachmentApi from "./api";
import type {
  AttachmentContext,
  GetAttachmentsResponse,
  GetFileLibraryParams,
  AttachUploadedFileParams,
  CreateLinkAttachmentParams,
  DeleteAttachmentParams,
} from "./types";

/**
 * Build query key for attachments
 * 
 * Consistent key pattern:
 * - ['attachments', doctype, name] for doc-level
 * - ['attachments', doctype, name, fieldname] for field-level
 */
export function getAttachmentsQueryKey(context: AttachmentContext): readonly unknown[] {
  if (context.fieldname) {
    return ["attachments", context.doctype, context.name, context.fieldname] as const;
  }
  return ["attachments", context.doctype, context.name] as const;
}

/**
 * Hook to fetch attachments for a document
 * 
 * @param context - Attachment context
 * @param options - React Query options
 * @returns Query result with attachments list
 */
export function useAttachments(
  context: AttachmentContext,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
) {
  return useQuery({
    queryKey: getAttachmentsQueryKey(context),
    queryFn: () => attachmentApi.getAttachments(context),
    enabled: options?.enabled !== false && !!context.name && !!context.doctype,
    staleTime: options?.staleTime ?? 30_000, // 30 seconds - don't refetch aggressively
  });
}

/**
 * Hook to browse file library with pagination
 * 
 * Uses infinite query for seamless pagination.
 * 
 * @param params - Search and filter parameters
 * @param options - React Query options
 * @returns Infinite query result
 */
export function useFileLibrary(
  params: GetFileLibraryParams = {},
  options?: {
    enabled?: boolean;
  }
) {
  return useInfiniteQuery({
    queryKey: ["file-library", params.query, params.mime_type, params.page_size],
    queryFn: ({ pageParam = 1 }) =>
      attachmentApi.getFileLibrary({
        ...params,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.has_more) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: options?.enabled !== false,
    staleTime: 60_000, // 1 minute - library doesn't change as frequently
  });
}

/**
 * Hook to attach an already uploaded file
 * 
 * Includes optimistic update and cache invalidation.
 * 
 * @param context - Attachment context
 * @returns Mutation hook
 */
export function useAttachFile(context: AttachmentContext) {
  const queryClient = useQueryClient();
  const queryKey = getAttachmentsQueryKey(context); // Precompute once

  return useMutation({
    mutationFn: async (params: Omit<AttachUploadedFileParams, "doctype" | "name" | "fieldname">) => {
      return attachmentApi.attachUploadedFile({
        doctype: context.doctype,
        name: context.name,
        fieldname: context.fieldname,
        ...params,
      });
    },
    onMutate: async (variables) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousData = queryClient.getQueryData<GetAttachmentsResponse>(queryKey);

      // Optimistically update
      queryClient.setQueryData<GetAttachmentsResponse>(queryKey, (old) => {
        if (!old) return old;

        // Create optimistic attachment (will be replaced by server response)
        const optimisticAttachment = {
          docname: `temp-${Date.now()}`,
          file_name: variables.file_name || variables.file_url.split("/").pop() || "file",
          file_url: variables.file_url,
          mime_type: null,
          file_size: 0,
          is_private: 0, // Backend returns 0/1, not boolean
          attached_to_field: context.fieldname ?? null,
          creation: new Date().toISOString(),
        };

        return {
          ...old,
          attachments: [optimisticAttachment, ...old.attachments],
        };
      });

      return { previousData };
    },
    onError: (_error, _variables, mutationContext) => {
      // Rollback on error - use mutationContext (not outer context)
      if (mutationContext?.previousData) {
        queryClient.setQueryData(queryKey, mutationContext.previousData);
      }
    },
    onSuccess: (data) => {
      // Update with server response
      queryClient.setQueryData(queryKey, data);
      
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
}

/**
 * Hook to create a link attachment
 * 
 * @param context - Attachment context
 * @returns Mutation hook
 */
export function useCreateLinkAttachment(context: AttachmentContext) {
  const queryClient = useQueryClient();
  const queryKey = getAttachmentsQueryKey(context); // Precompute once

  return useMutation({
    mutationFn: async (params: Omit<CreateLinkAttachmentParams, "doctype" | "name" | "fieldname">) => {
      return attachmentApi.createLinkAttachment({
        doctype: context.doctype,
        name: context.name,
        fieldname: context.fieldname,
        ...params,
      });
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<GetAttachmentsResponse>(queryKey);

      // Optimistic update
      queryClient.setQueryData<GetAttachmentsResponse>(queryKey, (old) => {
        if (!old) return old;

        const optimisticAttachment = {
          docname: `temp-${Date.now()}`,
          file_name: variables.label || variables.url,
          file_url: variables.url,
          mime_type: null,
          file_size: 0,
          is_private: 0, // Backend returns 0/1, not boolean
          attached_to_field: context.fieldname ?? null,
          creation: new Date().toISOString(),
        };

        return {
          ...old,
          attachments: [optimisticAttachment, ...old.attachments],
        };
      });

      return { previousData };
    },
    onError: (_error, _variables, mutationContext) => {
      if (mutationContext?.previousData) {
        queryClient.setQueryData(queryKey, mutationContext.previousData);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
}

/**
 * Hook to delete an attachment
 * 
 * @param context - Attachment context
 * @returns Mutation hook
 */
export function useDeleteAttachment(context: AttachmentContext) {
  const queryClient = useQueryClient();
  const queryKey = getAttachmentsQueryKey(context); // Precompute once

  return useMutation({
    mutationFn: async (params: Omit<DeleteAttachmentParams, "doctype" | "name">) => {
      return attachmentApi.deleteAttachment({
        doctype: context.doctype,
        name: context.name,
        ...params,
      });
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<GetAttachmentsResponse>(queryKey);

      // Optimistically remove attachment
      queryClient.setQueryData<GetAttachmentsResponse>(queryKey, (old) => {
        if (!old) return old;

        // Find attachment to remove (by docname or file_url)
        const filtered = old.attachments.filter((att) => {
          if (variables.file_docname) {
            return att.docname !== variables.file_docname;
          }
          if (variables.file_url) {
            return att.file_url !== variables.file_url;
          }
          return true;
        });

        return {
          ...old,
          attachments: filtered,
        };
      });

      return { previousData };
    },
    onError: (_error, _variables, mutationContext) => {
      if (mutationContext?.previousData) {
        queryClient.setQueryData(queryKey, mutationContext.previousData);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
}
